import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import apiClient from "../lib/api";
import {
  CaretLeft, Trophy, Target, Crown, TrendDown, CheckCircle,
  Warning, Lock, SoccerBall, Basketball, Baseball, ShieldCheck, Sparkle,
  Info, ChartBar, Share, Globe, ArrowSquareOut,
} from "@phosphor-icons/react";
import IGShareModal from "../components/IGShareModal";
import AdminSection from "../components/admin/AdminSection";
import { format } from "date-fns";
import TeamLogo from "../components/betiq/TeamLogo";
import LeagueBadge, { SPORT_ACCENT } from "../components/LeagueBadge";
import { resolveTeamLogo } from "../lib/resolveTeamLogo";
import InfoTooltip from "../components/InfoTooltip";

const SPORT_ICON = { football: SoccerBall, nba: Basketball, mlb: Baseball };

/* ─────────────────────────────────────────────────────────────
   BET EXPLAINER — Explicación para principiantes
───────────────────────────────────────────────────────────── */
function buildBetExplanation(pick) {
  const mkt = (pick.market || "").toLowerCase().trim();
  const sel = (pick.pick_selection || "").trim();
  const home = pick.home_team || "Local";
  const away = pick.away_team || "Visitante";
  const sport = pick.sport || "";

  const moreM  = /más de\s*([\d.]+)/i.exec(sel);
  const lessM  = /menos de\s*([\d.]+)/i.exec(sel);
  const overM  = /over\s*([\d.]+)/i.exec(sel);
  const underM = /under\s*([\d.]+)/i.exec(sel);
  const line   = moreM?.[1] || overM?.[1] || lessM?.[1] || underM?.[1];
  const isOver = !!(moreM || overM);
  const isUnder= !!(lessM || underM);
  const dir    = isOver ? "MÁS DE" : isUnder ? "MENOS DE" : null;
  const hMatch = /([+-][\d.]+)/.exec(sel);
  const hVal   = hMatch?.[1];

  // MONEYLINE
  if (mkt.includes("moneyline") || mkt.includes("ganador")) {
    const team = sel.replace(/\s*ml\s*/i, "").trim() || home;
    return {
      icon: "trophy", title: "Moneyline — Ganador directo",
      where: "En tu casa de apuestas busca la sección del partido y toca el nombre del equipo. Suele ser la primera opción que ves.",
      explain: `Apostamos a que ${team} GANA el partido sin importar el marcador. Si ganan por 1 o por 10, la jugada GANA.`,
      win:  `${team} gana el partido por cualquier marcador.`,
      lose: `${team} pierde el partido.`,
    };
  }

  // 1X2
  if (mkt.includes("1x2") || mkt.includes("resultado")) {
    const map = { "1": `${home} gana`, "X": "Empate", "2": `${away} gana` };
    const meaning = map[sel] || `resultado "${sel}"`;
    return {
      icon: "soccer", title: "1X2 — Resultado del partido",
      where: "Es lo primero que aparece al abrir el partido: verás tres botones 1, X, 2. Toca el que diga esta jugada.",
      explain: `La apuesta más clásica del fútbol: 1 = gana ${home}, X = empate, 2 = gana ${away}.\nEn este pick apostamos a: "${sel}" → ${meaning}.`,
      win:  `${meaning} al final de los 90 minutos.`,
      lose: `Cualquier otro resultado diferente a "${sel}".`,
    };
  }

  // OVER/UNDER totales
  if ((isOver || isUnder) && line && (mkt.includes("over") || mkt.includes("under") || mkt.includes("total") || mkt.includes("carrera") || mkt.includes("gol") || mkt.includes("punto") || mkt.includes("hit") || mkt.includes("touch") || mkt.includes("rebote"))) {
    const unitMap = [["carrera","carreras"],["gol","goles"],["hit","hits"],["touch","touchdowns"],["rebote","rebotes"],["punto","puntos"]];
    let unit = sport === "mlb" ? "carreras" : sport === "nba" ? "puntos" : "goles";
    for (const [k,v] of unitMap) { if (mkt.includes(k)) { unit = v; break; } }
    const n = parseFloat(line);
    const up = Math.ceil(n), down = Math.floor(n);
    return {
      icon: "chart", title: `Over/Under — Total de ${unit}`,
      where: `Busca la sección "Goles" o "Totales" del partido y encuentra la línea "Más de ${line}" o "Menos de ${line}". Tócala.`,
      explain: `No apostamos a quién gana — apostamos a cuántos ${unit} se anotan en total entre los DOS equipos sumados. En este pick apostamos que habrá ${dir} ${line} ${unit}.`,
      win:  isOver  ? `Los dos equipos suman ${up} ${unit} o más. Ej: marcador 5-3 = 8 ${unit} → GANA porque 8 > ${line}.`
                    : `Los dos equipos suman ${down} ${unit} o menos. Ej: marcador 3-2 = 5 ${unit} → GANA porque 5 < ${line}.`,
      lose: isOver  ? `El partido termina con ${down} ${unit} o menos en total.`
                    : `El partido termina con ${up} ${unit} o más en total.`,
    };
  }

  // RUN LINE (béisbol)
  if (mkt.includes("run line") || mkt.includes("runline")) {
    const isFav = sel.includes("-");
    const team = sel.split(/[+-]/)[0].trim() || home;
    return {
      icon: "baseball", title: "Run Line — Spread de béisbol",
      where: "Busca la sección \"Run Line\" o \"Spread\" del partido de béisbol. Verás el equipo con -1.5 o +1.5 al lado.",
      explain: isFav
        ? `Apostamos que ${team} no solo GANA, sino que gana por 2 o más carreras de diferencia. Si gana por 1 sola carrera, la jugada PIERDE.`
        : `Apostamos que ${team} gana el partido, O si pierde, que lo hace por solo 1 carrera. Tiene más margen de error.`,
      win:  isFav ? `${team} gana por 2 o más carreras. Ej: 6-4 (diferencia de 2) ✓`
                  : `${team} gana, o pierde por solo 1 carrera. Ej: 4-5 ✓`,
      lose: isFav ? `${team} gana por solo 1 carrera, o pierde el partido.`
                  : `${team} pierde por 2 o más carreras.`,
    };
  }

  // SPREAD / HANDICAP
  if (mkt.includes("spread") || mkt.includes("handicap") || mkt.includes("hándicap") || mkt.includes("línea")) {
    const isFav = hVal?.startsWith("-");
    const absVal = hVal?.replace(/[+-]/,"") || "X";
    const team = sel.split(/[+-]/)[0].trim() || home;
    return {
      icon: "spread", title: "Spread / Hándicap — Ventaja virtual",
      where: "Busca la sección \"Hándicap\" o \"Spread\" del partido. Verás el equipo con un número como -5.5 o +5.5.",
      explain: `Se agrega una ventaja virtual de puntos para equilibrar la apuesta. Si el spread es ${hVal || sel}, apostamos que ${team} supera esa diferencia.`,
      win:  isFav ? `${team} gana por más de ${absVal} puntos/carreras.`
                  : `${team} gana, o pierde por menos de ${absVal} puntos/carreras.`,
      lose: isFav ? `${team} gana por menos de ${absVal} o pierde.`
                  : `${team} pierde por ${absVal} o más puntos/carreras.`,
    };
  }

  // AMBOS ANOTAN
  if (mkt.includes("ambos") || mkt.includes("btts")) {
    const isYes = /s[ií]/i.test(sel) || !sel || sel === "";
    return {
      icon: "soccer", title: "Ambos Anotan (BTTS)",
      where: "Busca la sección \"Ambos equipos anotan\" o \"BTTS\". Verás dos botones: Sí / No.",
      explain: isYes
        ? `Apostamos que TANTO ${home} COMO ${away} metan al menos 1 gol cada uno. No importa quién gane.`
        : `Apostamos que al menos un equipo NO anota ningún gol.`,
      win:  isYes ? `Marcadores como 2-1, 1-1, 1-2 — ambos equipos marcaron.`
                  : `Marcadores como 1-0, 0-2 — algún equipo no anotó.`,
      lose: isYes ? `Marcadores como 1-0 o 0-2 — algún equipo no marcó.`
                  : `Marcadores donde ambos equipos anotaron (ej: 2-1).`,
    };
  }

  // DOBLE OPORTUNIDAD
  if (mkt.includes("doble")) {
    return {
      icon: "shield", title: "Doble Oportunidad — Apuesta con margen",
      where: "Busca la sección \"Doble oportunidad\". Verás opciones como 1X, 12, X2.",
      explain: `Cubre DOS de los tres posibles resultados del partido. Más segura pero paga menos. La selección "${sel}" indica los dos resultados cubiertos.`,
      win:  `Cualquiera de los dos resultados incluidos en "${sel}".`,
      lose: `El único resultado que queda fuera.`,
    };
  }

  // TOTAL TARJETAS
  if (mkt.includes("tarjeta") && dir && line) {
    const n = parseFloat(line);
    return {
      icon: "card", title: "Total de Tarjetas",
      where: "Busca la sección \"Tarjetas\" del partido y la línea de Más/Menos.",
      explain: `No apostamos al resultado del partido sino al número de tarjetas que muestra el árbitro (amarillas + rojas). Apostamos que habrá ${dir} ${line} tarjetas en total.`,
      win:  isOver  ? `El árbitro muestra ${Math.ceil(n)} tarjetas o más en total.`
                    : `El árbitro muestra ${Math.floor(n)} tarjetas o menos en total.`,
      lose: isOver  ? `Se muestran ${Math.floor(n)} tarjetas o menos.`
                    : `Se muestran ${Math.ceil(n)} tarjetas o más.`,
    };
  }

  return null;
}

function BetExplainer({ pick }) {
  const [open, setOpen] = useState(true);
  const data = buildBetExplanation(pick);
  if (!data) return null;

  const iconEl = {
    trophy:   <Trophy weight="fill" className="w-4 h-4" style={{ color: "#D4AF37" }} />,
    soccer:   <SoccerBall weight="fill" className="w-4 h-4" style={{ color: "#D4AF37" }} />,
    baseball: <Baseball weight="fill" className="w-4 h-4" style={{ color: "#D4AF37" }} />,
    chart:    <ChartBar weight="fill" className="w-4 h-4" style={{ color: "#D4AF37" }} />,
    spread:   <Target weight="fill" className="w-4 h-4" style={{ color: "#D4AF37" }} />,
    shield:   <ShieldCheck weight="fill" className="w-4 h-4" style={{ color: "#D4AF37" }} />,
    card:     <Warning weight="fill" className="w-4 h-4" style={{ color: "#D4AF37" }} />,
  }[data.icon] || <Info weight="fill" className="w-4 h-4" style={{ color: "#D4AF37" }} />;

  return (
    <div className="mb-6 rounded-2xl overflow-hidden" style={{ background: "linear-gradient(145deg,#13131A,#0F0F14)", border: "1px solid rgba(212,175,55,0.18)", boxShadow: "0 4px 20px rgba(0,0,0,0.4)" }}>
      {/* Header clicable */}
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-3 px-5 py-4"
        style={{ borderBottom: open ? "1px solid rgba(255,255,255,0.05)" : "none" }}
        data-testid="bet-explainer-toggle"
      >
        <div className="flex items-center gap-3">
          {iconEl}
          <div className="text-left">
            <div className="text-[10px] uppercase tracking-[0.22em] font-black mb-0.5" style={{ color: "#D4AF37" }}>
              ¿Qué significa esta jugada?
            </div>
            <div className="text-xs font-semibold text-zinc-300">{data.title}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-[9px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full" style={{ background: "rgba(212,175,55,0.12)", color: "#D4AF37", border: "1px solid rgba(212,175,55,0.2)" }}>
            Para principiantes
          </span>
          <span className="text-zinc-500 text-xs">{open ? "▲" : "▼"}</span>
        </div>
      </button>

      {open && (
        <div className="px-5 py-4 space-y-4">
          {/* Explicación */}
          <p className="text-sm text-zinc-300 leading-relaxed whitespace-pre-line">{data.explain}</p>

          {/* Dónde encontrarla */}
          {data.where && (
            <div className="rounded-xl p-3.5 mb-3" style={{ background: "rgba(212,175,55,0.06)", border: "1px solid rgba(212,175,55,0.20)" }}>
              <div className="text-[9px] uppercase tracking-widest font-black mb-1.5" style={{ color: "#D4AF37" }}>Dónde ponerla</div>
              <p className="text-xs text-zinc-300 leading-relaxed">{data.where}</p>
            </div>
          )}

          {/* Cuándo ganas / pierdes */}
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="rounded-xl p-3.5" style={{ background: "rgba(16,185,129,0.06)", border: "1px solid rgba(16,185,129,0.18)" }}>
              <div className="text-[9px] uppercase tracking-widest font-black text-emerald-400 mb-1.5">Esta jugada GANA si…</div>
              <p className="text-xs text-zinc-300 leading-relaxed">{data.win}</p>
            </div>
            <div className="rounded-xl p-3.5" style={{ background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.18)" }}>
              <div className="text-[9px] uppercase tracking-widest font-black text-red-400 mb-1.5">Esta jugada PIERDE si…</div>
              <p className="text-xs text-zinc-300 leading-relaxed">{data.lose}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}



const STATUS_STYLE = {
  pending: { label: "PENDIENTE", cls: "bg-yellow-500/10 text-yellow-400 border-yellow-500/25" },
  won: { label: "GANADA", cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/25" },
  lost: { label: "PERDIDA", cls: "bg-red-500/10 text-red-400 border-red-500/25" },
  void: { label: "ANULADA", cls: "bg-zinc-500/10 text-zinc-400 border-zinc-500/25" },
};

const LABEL_STYLE = {
  strong: { label: "SÓLIDA", cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/25" },
  good: { label: "BUENA", cls: "bg-blue-500/10 text-blue-400 border-blue-500/25" },
  questionable: { label: "DUDOSA", cls: "bg-yellow-500/10 text-yellow-400 border-yellow-500/25" },
  weak: { label: "DÉBIL", cls: "bg-red-500/10 text-red-400 border-red-500/25" },
};

const AI_LABEL_STYLE = {
  buen_pick: { label: "Jugada recomendada", cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/25", icon: CheckCircle },
  dudoso: { label: "Jugada dudosa", cls: "bg-yellow-500/10 text-yellow-400 border-yellow-500/25", icon: Warning },
  no_recomendable: { label: "No recomendable", cls: "bg-red-500/10 text-red-400 border-red-500/25", icon: Warning },
};

export default function PickDetail() {
  const { id } = useParams();
  const [pick, setPick] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showShare, setShowShare] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiClient.get(`/picks/${id}`);
        setPick(res.data);
      } catch (err) {
        console.error("[PickDetail] fetch failed:", err?.response?.data || err.message);
        setPick(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-4 lg:px-8 pt-8 pb-16">
        <div className="surface rounded-xl h-96 animate-pulse" />
      </div>
    );
  }

  if (!pick) {
    return (
      <div className="max-w-5xl mx-auto px-4 lg:px-8 pt-8 pb-16 text-center">
        <div className="text-zinc-400">Jugada no encontrada</div>
        <Link to="/jugadas" className="btn-secondary mt-4 inline-flex">Volver a jugadas</Link>
      </div>
    );
  }

  const Icon = SPORT_ICON[pick.sport] || Target;
  const statusStyle = STATUS_STYLE[pick.status];
  const labelStyle = pick.scoring && LABEL_STYLE[pick.scoring.label];
  const aiStyle = pick.ai_validation && AI_LABEL_STYLE[pick.ai_validation.label];
  const isLocked = pick.locked;

  // Auto-resolve team logos from ESPN CDN when not stored in DB
  const homeLogo = pick.home_logo || resolveTeamLogo(pick.sport, pick.home_team);
  const awayLogo = pick.away_logo || resolveTeamLogo(pick.sport, pick.away_team);

  return (
    <div className="max-w-6xl mx-auto px-4 lg:px-8 pt-6 pb-16">
      {/* Back */}
      <Link to="/jugadas" className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-white mb-6 uppercase tracking-wider" data-testid="pick-detail-back">
        <CaretLeft className="w-3.5 h-3.5" /> Volver
      </Link>

      {/* Header */}
      <div className="rounded-2xl p-6 lg:p-8 relative overflow-hidden mb-6"
        style={{
          background: "linear-gradient(145deg, #1C1C22 0%, #14141A 100%)",
          border: `1px solid ${SPORT_ACCENT[pick.sport]?.border || "rgba(212,175,55,0.2)"}`,
          boxShadow: `0 8px 32px rgba(0,0,0,0.6), 0 0 0 0 transparent`,
        }}>
        {/* Sport-color top accent */}
        <div style={{
          position: "absolute", top: 0, left: 0, right: 0, height: 3,
          background: `linear-gradient(90deg, transparent, ${SPORT_ACCENT[pick.sport]?.color || "#D4AF37"}, transparent)`,
        }} />
        <div className="absolute inset-0 grid-bg opacity-20 pointer-events-none" />
        <div className="relative">
          {/* Liga + logos de liga + estado */}
          <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 px-3 py-2 rounded-xl"
                style={{ background: SPORT_ACCENT[pick.sport]?.bg || "rgba(212,175,55,0.06)", border: `1px solid ${SPORT_ACCENT[pick.sport]?.border || "rgba(212,175,55,0.2)"}` }}>
                <LeagueBadge sport={pick.sport} league={pick.league} size="lg" />
                <div>
                  <div className="text-[9px] uppercase tracking-[0.25em] font-bold" style={{ color: SPORT_ACCENT[pick.sport]?.color || "#D4AF37" }}>
                    {pick.sport === "nba" ? "Baloncesto" : pick.sport === "mlb" ? "Béisbol" : "Fútbol"}
                  </div>
                  <div className="text-xs font-semibold text-zinc-300">
                    {pick.league === "NBA" || pick.league === "MLB" ? pick.sport.toUpperCase() : pick.league}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <span className={`chip ${statusStyle.cls}`}>{statusStyle.label}</span>
              {pick.access_tier === "vip" && (
                <span className="chip-yellow">
                  <Crown weight="fill" className="w-3 h-3" /> VIP
                </span>
              )}
              {pick.status === "won" && (
                <button
                  data-testid="pick-share-victory-btn"
                  onClick={() => setShowShare(true)}
                  className="chip bg-emerald-500/10 text-emerald-400 border-emerald-500/20 hover:bg-emerald-500/20 transition-colors cursor-pointer"
                >
                  <Share weight="bold" className="w-3 h-3" /> Compartir
                </button>
              )}
            </div>
          </div>

          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
            {/* Teams con logos */}
            <div className="flex-1">
              <div className="flex items-center gap-4 mb-3">
                <TeamLogo logoUrl={homeLogo} teamName={pick.home_team} size={52} rounded="md" />
                <div className="text-display font-black text-2xl lg:text-4xl tracking-tighter leading-tight" data-testid="pick-detail-home">
                  {pick.home_team}
                </div>
              </div>
              <div className="text-zinc-400 text-xs ml-16 font-mono uppercase tracking-widest font-black mb-3">— vs —</div>
              <div className="flex items-center gap-4">
                <TeamLogo logoUrl={awayLogo} teamName={pick.away_team} size={52} rounded="md" />
                <div className="text-display font-black text-2xl lg:text-4xl tracking-tighter leading-tight text-zinc-300" data-testid="pick-detail-away">
                  {pick.away_team}
                </div>
              </div>
              {pick.match_date && (
                <div className="text-xs text-zinc-500 mt-5 font-mono uppercase tracking-wider">
                  {format(new Date(pick.match_date), "EEE dd MMM yyyy · HH:mm")}
                </div>
              )}
              {pick.result_note && (
                <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-md"
                  style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-widest font-bold">Resultado</div>
                  <div className="number-ticker font-bold">{pick.result_note}</div>
                </div>
              )}
            </div>

            {/* Pick badge */}
            <div className="rounded-xl p-5 min-w-0 flex-1"
              style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
              <div className="text-[10px] text-zinc-500 uppercase tracking-[0.2em] font-bold mb-2 flex items-center gap-1.5">
                {pick.market}
                <InfoTooltip market={pick.market} sport={pick.sport} />
              </div>
              <div className="text-display font-bold text-xl mb-2">{pick.pick_selection}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Odds Comparator — visible to ALL users (selling point) */}
      {pick.odds_comparison?.length > 0 && (
        <OddsComparator comparison={pick.odds_comparison} bestOdds={pick.best_odds} />
      )}

      {isLocked ? (
        <LockedBlock pick={pick} onUnlocked={() => window.location.reload()} />
      ) : (
        <>
          {/* Notas exclusivas de Ronald — destacadas con efecto parpadeante */}
          {pick.stats?.notes && (
            <div className="pickdetail-notes mb-6" data-testid="pick-detail-notes">
              <div className="pickdetail-notes-label">
                <span className="pickdetail-notes-dot" />
                NOTA EXCLUSIVA DE RONALD
              </div>
              <div className="pickdetail-notes-text">{pick.stats.notes}</div>
              <style>{`
                .pickdetail-notes {
                  padding: 16px 20px; border-radius: 14px;
                  background: linear-gradient(135deg, rgba(212,175,55,0.13), rgba(212,175,55,0.04));
                  border: 1.5px solid rgba(212,175,55,0.45);
                  animation: pdn-pulse 2.4s ease-in-out infinite;
                  position: relative; overflow: hidden;
                }
                .pickdetail-notes::before {
                  content: ""; position: absolute; top: 0; left: 0; right: 0; height: 2px;
                  background: linear-gradient(90deg, transparent, #D4AF37, transparent);
                  animation: pdn-shimmer 3s ease-in-out infinite;
                }
                .pickdetail-notes-label {
                  display: inline-flex; align-items: center; gap: 8px;
                  font-size: 10.5px; font-weight: 900; letter-spacing: 0.22em;
                  color: #D4AF37; margin-bottom: 8px; text-transform: uppercase;
                }
                .pickdetail-notes-dot {
                  width: 8px; height: 8px; border-radius: 999px; background: #D4AF37;
                  box-shadow: 0 0 0 0 rgba(212,175,55,0.7);
                  animation: pdn-dot 1.4s ease-in-out infinite;
                }
                .pickdetail-notes-text {
                  font-size: 15px; line-height: 1.6; color: #FAE8B0; font-weight: 600;
                }
                @keyframes pdn-pulse {
                  0%, 100% { box-shadow: 0 0 0 0 rgba(212,175,55,0.05), 0 0 12px rgba(212,175,55,0.10); }
                  50%      { box-shadow: 0 0 0 6px rgba(212,175,55,0.10), 0 0 24px rgba(212,175,55,0.25); }
                }
                @keyframes pdn-dot {
                  0%, 100% { box-shadow: 0 0 0 0 rgba(212,175,55,0.7); }
                  50%      { box-shadow: 0 0 0 7px rgba(212,175,55,0); }
                }
                @keyframes pdn-shimmer {
                  0%, 100% { opacity: 0.35; }
                  50%      { opacity: 1; }
                }
              `}</style>
            </div>
          )}

          {/* EXPLICACIÓN PARA PRINCIPIANTES */}
          <BetExplainer pick={pick} />

          {/* Nivel de confianza de Ronald (1-10) */}
          {pick.confidence && (
            <div className="mb-6 rounded-2xl p-5 overflow-hidden relative"
              style={{
                background: "linear-gradient(145deg, #1C1C22 0%, #16161A 100%)",
                border: "1px solid rgba(212,175,55,0.2)",
                boxShadow: "0 4px 20px rgba(0,0,0,0.4)"
              }}>
              <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: "linear-gradient(90deg, #D4AF37, #10B981, #D4AF37)" }} />
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] font-black mb-1" style={{ color: "#D4AF37" }}>
                    Confianza de Ronald
                  </div>
                  <div className="text-sm text-zinc-400">Nivel de convicción del analista en esta señal</div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="font-black leading-none mb-1"
                    style={{
                      fontSize: 48,
                      color: pick.confidence >= 7 ? "#10B981" : pick.confidence >= 5 ? "#F59E0B" : "#D4AF37"
                    }}
                    data-testid="pick-confidence-score">
                    {pick.confidence}
                  </div>
                  <div className="text-[10px] font-black uppercase tracking-wider"
                    style={{ color: pick.confidence >= 7 ? "#10B981" : pick.confidence >= 5 ? "#F59E0B" : "#D4AF37" }}>
                    {pick.confidence >= 8 ? "MUY ALTA" : pick.confidence >= 6 ? "ALTA" : pick.confidence >= 4 ? "MEDIA" : "MODERADA"}
                  </div>
                </div>
              </div>
              {/* Barra visual */}
              <div className="mt-3 h-1.5 rounded-full overflow-hidden" style={{ background: "#27272A" }}>
                <div className="h-full rounded-full transition-all duration-700"
                  style={{
                    width: `${pick.confidence * 10}%`,
                    background: pick.confidence >= 7 ? "#10B981" : pick.confidence >= 5 ? "#F59E0B" : "#D4AF37"
                  }} />
              </div>
            </div>
          )}

          <div className="space-y-6">
            {/* Análisis de Ronald */}
            {pick.analysis && (
              <AdminSection icon={Sparkle} title="Análisis de Ronald" subtitle="La lectura del partido" color="amber">
                <p className="text-zinc-200 leading-relaxed" data-testid="pick-detail-analysis">
                  {pick.analysis}
                </p>
                {/* Pie de análisis con branding */}
                <div className="mt-4 pt-4 border-t border-white/[0.06] flex items-center gap-2">
                  <ShieldCheck weight="fill" className="w-4 h-4 flex-shrink-0" style={{ color: "#D4AF37" }} />
                  <p className="text-xs text-zinc-500">
                    Análisis basado en la experiencia de Ronald, histórico de resultados y factores de mercado. El objetivo es maximizar aciertos con gestión responsable del riesgo.
                  </p>
                </div>
              </AdminSection>
            )}

            {/* Fortalezas del análisis — solo si hay y solo las positivas */}
            {pick.ai_validation?.pros?.length > 0 && (
              <AdminSection icon={CheckCircle} title="Factores a favor" subtitle="Por qué esta jugada" color="emerald">
                <ul className="space-y-2.5">
                  {pick.ai_validation.pros.map((p, i) => (
                    <li key={i} className="text-sm text-zinc-300 flex items-start gap-2">
                      <span className="text-emerald-400 mt-0.5 flex-shrink-0">→</span>
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </AdminSection>
            )}

              {/* Stats */}
              {pick.stats && Object.keys(pick.stats).some(k => pick.stats[k]) && (
                <AdminSection icon={ChartBar} title="Contexto del partido" subtitle="Datos y estadísticas" color="sky">
                  <div className="grid md:grid-cols-2 gap-6">
                    {(pick.stats.form_home || pick.stats.form_away) && (
                      <div>
                        <div className="text-xs text-zinc-500 uppercase tracking-wider font-bold mb-3">Forma reciente</div>
                        <FormLine label={pick.home_team} form={pick.stats.form_home} />
                        <FormLine label={pick.away_team} form={pick.stats.form_away} />
                      </div>
                    )}
                    {pick.stats.h2h_summary && (
                      <div>
                        <div className="text-xs text-zinc-500 uppercase tracking-wider font-bold mb-3">Head to Head</div>
                        <div className="text-sm text-zinc-200">{pick.stats.h2h_summary}</div>
                      </div>
                    )}
                    {(pick.stats.recent_goals_home != null || pick.stats.recent_goals_away != null) && (
                      <div>
                        <div className="text-xs text-zinc-500 uppercase tracking-wider font-bold mb-3">Promedio ofensivo</div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div><span className="text-zinc-500">Local: </span><span className="font-mono text-white">{pick.stats.recent_goals_home ?? "—"}</span></div>
                          <div><span className="text-zinc-500">Visitante: </span><span className="font-mono text-white">{pick.stats.recent_goals_away ?? "—"}</span></div>
                        </div>
                      </div>
                    )}
                    {pick.stats.injuries && (
                      <div>
                        <div className="text-xs text-zinc-500 uppercase tracking-wider font-bold mb-3">Bajas / Lesiones</div>
                        <div className="text-sm text-zinc-200">{pick.stats.injuries}</div>
                      </div>
                    )}
                    {pick.stats.key_factors?.length > 0 && (
                      <div className="md:col-span-2">
                        <div className="text-xs text-zinc-500 uppercase tracking-wider font-bold mb-3">Factores clave</div>
                        <div className="flex flex-wrap gap-2">
                          {pick.stats.key_factors.map((f, i) => (
                            <span key={i} className="chip-muted">{f}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </AdminSection>
              )}

              <section className="surface rounded-xl p-5 text-xs text-zinc-500">
                <Info weight="regular" className="w-4 h-4 text-zinc-500 inline-block mr-1.5 -mt-0.5" />
                Los análisis de Ronald combinan experiencia en betting, datos históricos y lectura de mercado. Las apuestas implican riesgo — gestiona tu bankroll con responsabilidad.
              </section>
          </div>
        </>
      )}
      {showShare && <IGShareModal pick={pick} onClose={() => setShowShare(false)} />}
    </div>
  );
}

function OddsComparator({ comparison, bestOdds }) {
  const [activeRegion, setActiveRegion] = useState(null);

  if (!comparison?.length) return null;

  // Group by region — map 'global'/'us' together as "Global / USA"
  const regionGroups = {
    eu:     { label: "Europa",       flag: "EU",  items: comparison.filter(b => b.region === "eu").slice(0, 3) },
    global: { label: "Global / USA", flag: "USA", items: comparison.filter(b => b.region === "global" || b.region === "us").slice(0, 3) },
    latam:  { label: "LatAm",        flag: "LAT", items: comparison.filter(b => b.region === "latam").slice(0, 3) },
  };

  const available = Object.entries(regionGroups).filter(([, g]) => g.items.length > 0);
  const current = activeRegion || available[0]?.[0];
  const currentGroup = regionGroups[current];

  // Best odds globally
  const overallBest = [...comparison].sort((a, b) => b.odds - a.odds)[0];

  return (
    <section className="mb-6 rounded-2xl overflow-hidden" data-testid="odds-comparator"
      style={{ background: "#16161A", border: "1px solid rgba(212,175,55,0.2)", boxShadow: "0 8px 32px rgba(0,0,0,0.5)" }}>

      {/* Header */}
      <div className="px-5 pt-5 pb-4" style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-yellow-400/10 border border-yellow-400/20 flex items-center justify-center flex-shrink-0">
              <ChartBar weight="fill" className="w-5 h-5 text-yellow-400" />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] font-black" style={{ color: "#D4AF37" }}>
                Mejores cuotas por región
              </div>
              <div className="text-xs text-zinc-500">{comparison.length} casas analizadas · Top 3 por zona</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[9px] text-zinc-400 uppercase tracking-wider font-bold mb-0.5">Mejor cuota global</div>
            <div className="text-3xl font-black number-ticker text-emerald-400 leading-none" data-testid="best-odds-value">
              {overallBest?.odds?.toFixed(2)}
            </div>
            <div className="text-xs text-zinc-400 mt-0.5">{overallBest?.bookmaker}</div>
          </div>
        </div>
      </div>

      {/* Region tabs */}
      <div className="flex gap-1.5 p-3" style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
        {available.map(([key, group]) => (
          <button
            key={key}
            onClick={() => setActiveRegion(key)}
            data-testid={`odds-tab-${key}`}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-2 rounded-xl text-[11px] font-black uppercase tracking-wider transition-all duration-200 ${
              current === key
                ? "text-black"
                : "text-zinc-500 hover:text-zinc-300 border border-transparent"
            }`}
            style={current === key
              ? { background: "linear-gradient(135deg, #D4AF37, #B8962E)", border: "1px solid rgba(212,175,55,0.5)", boxShadow: "0 2px 12px rgba(212,175,55,0.25)" }
              : { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }
            }
          >
            <Globe weight="bold" className="w-3.5 h-3.5 flex-shrink-0" />
            <span>{group.flag}</span>
            <span className="hidden sm:inline">— {group.label}</span>
          </button>
        ))}
      </div>

      {/* Bookmaker rows */}
      <div className="p-4 space-y-2">
        {currentGroup?.items?.length > 0 ? currentGroup.items.map((b, i) => {
          const isBest = i === 0;
          const maxO = Math.max(...currentGroup.items.map(x => x.odds));
          const minO = Math.min(...currentGroup.items.map(x => x.odds));
          const range = maxO - minO || 0.01;
          const barPct = ((b.odds - minO) / range) * 55 + 30;
          return (
            <div
              key={b.bookmaker}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                isBest
                  ? "border"
                  : "hover:bg-white/[0.025] border border-transparent"
              }`}
              style={isBest ? { background: "rgba(16,185,129,0.07)", borderColor: "rgba(16,185,129,0.22)" } : {}}
              data-testid={isBest ? "odds-best-row" : `odds-row-${i}`}
            >
              {/* Rank */}
              <span className={`text-[11px] font-black w-5 text-center flex-shrink-0 font-mono ${isBest ? "text-emerald-400" : "text-zinc-700"}`}>
                {i + 1}
              </span>
              {/* Bookmaker name */}
              <span className={`text-sm font-semibold flex-shrink-0 w-28 truncate ${isBest ? "text-white" : "text-zinc-300"}`}>
                {b.bookmaker}
              </span>
              {/* Odds bar */}
              <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.05)" }}>
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{
                    width: `${barPct}%`,
                    background: isBest ? "#10B981" : "#3F3F46",
                  }}
                />
              </div>
              {/* Odds value */}
              <span className={`font-black font-mono text-lg w-14 text-right number-ticker flex-shrink-0 ${isBest ? "text-emerald-400" : "text-zinc-100"}`}>
                {Number(b.odds || 0).toFixed(2)}
              </span>
              {/* Action / badge */}
              <div className="w-20 flex-shrink-0 flex justify-end">
                {b.url ? (
                  <a
                    href={b.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[10px] font-bold px-2.5 py-1 rounded-lg transition-all"
                    style={{ background: "rgba(212,175,55,0.1)", color: "#D4AF37", border: "1px solid rgba(212,175,55,0.25)" }}
                    onClick={e => e.stopPropagation()}
                    data-testid={`odds-bet-link-${i}`}
                  >
                    Apostar <ArrowSquareOut weight="bold" className="w-2.5 h-2.5" />
                  </a>
                ) : isBest ? (
                  <span className="text-[8px] font-black px-2 py-0.5 rounded-full uppercase tracking-wider whitespace-nowrap"
                    style={{ background: "rgba(16,185,129,0.12)", color: "#10B981", border: "1px solid rgba(16,185,129,0.25)" }}>
                    MEJOR
                  </span>
                ) : null}
              </div>
            </div>
          );
        }) : (
          <div className="text-center py-6 text-zinc-400 text-sm">
            No hay cuotas disponibles para esta región
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-5 pb-4 pt-2 flex items-center justify-between" style={{ borderTop: "1px solid rgba(255,255,255,0.04)" }}>
        <span className="text-xs text-zinc-400">Cuotas orientativas · Actualización periódica</span>
        <span className="text-[10px] text-zinc-400">Juega con responsabilidad</span>
      </div>
    </section>
  );
}

function ScoringTile({ label, value, suffix = "", prefix = "", tone = "neutral" }) {
  const color =
    tone === "positive" ? "text-emerald-400" :
    tone === "negative" ? "text-red-400" :
    "text-white";
  return (
    <div className="surface rounded-xl p-5">
      <div className="text-[10px] text-zinc-500 uppercase tracking-[0.2em] font-bold mb-2">{label}</div>
      <div className={`number-ticker text-2xl font-bold ${color}`}>
        {prefix}{value}{suffix}
      </div>
    </div>
  );
}

function FormLine({ label, form }) {
  if (!form) return null;
  return (
    <div className="flex items-center justify-between mb-2 text-sm">
      <span className="text-zinc-300 truncate max-w-[160px]">{label}</span>
      <div className="flex gap-1">
        {form.split("").map((c, i) => {
          const clr = c === "W" ? "bg-emerald-500 text-white" : c === "L" ? "bg-red-500 text-white" : "bg-zinc-600 text-zinc-200";
          return (
            <div key={i} className={`w-6 h-6 rounded flex items-center justify-center text-[10px] font-bold ${clr}`}>{c}</div>
          );
        })}
      </div>
    </div>
  );
}

function LockedBlock({ pick, onUnlocked }) {
  const isPickVip = pick?.is_paid_pick && pick?.pick_price;
  const [unlockOpen, setUnlockOpen] = useState(false);

  if (isPickVip) {
    return (
      <>
        <div className="surface rounded-2xl p-8 lg:p-12 text-center relative overflow-hidden"
             style={{
               background: "linear-gradient(135deg, rgba(212,175,55,0.10) 0%, rgba(212,175,55,0.02) 100%)",
               border: "1.5px solid rgba(212,175,55,0.4)",
             }}>
          <div className="absolute inset-0 grid-bg opacity-20 pointer-events-none" />
          <div className="relative">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4"
                 style={{ background: "rgba(212,175,55,0.15)", border: "1.5px solid rgba(212,175,55,0.5)" }}>
              <Crown weight="fill" className="w-8 h-8" style={{ color: "#D4AF37" }} />
            </div>
            <div className="text-[11px] tracking-[0.3em] uppercase font-black mb-2" style={{ color: "#D4AF37" }}>
              💎 Pick VIP — Exclusivo
            </div>
            <h2 className="text-display font-black text-3xl tracking-tight mb-3 text-white">
              Pick individual de pago
            </h2>
            <p className="text-zinc-300 max-w-md mx-auto text-sm mb-2">
              Esta jugada se vende aparte. Desbloquéala una sola vez para ver la selección y el análisis completo hasta el final del partido.
            </p>
            <div className="text-5xl font-black my-5" style={{ color: "#D4AF37" }}>
              ${pick.pick_price?.toFixed(0)}
              <span className="text-sm font-normal text-zinc-500 ml-1">USD</span>
            </div>
            <button
              onClick={() => setUnlockOpen(true)}
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl text-sm font-black tracking-widest uppercase transition-all hover:scale-[1.02]"
              style={{
                background: "linear-gradient(135deg, #D4AF37 0%, #B8941F 100%)",
                color: "#0A0A0F",
                boxShadow: "0 8px 32px -6px rgba(212,175,55,0.5)",
              }}
              data-testid="pick-vip-unlock-btn"
            >
              🔓 Desbloquear ahora
            </button>
            <p className="text-[11px] text-zinc-500 mt-4">
              Pago único · Acceso hasta que termine el partido · Tarjeta o pago manual
            </p>
          </div>
        </div>
        {unlockOpen && (
          <PickVipUnlockModal
            pick={pick}
            onClose={() => setUnlockOpen(false)}
            onSuccess={onUnlocked}
          />
        )}
      </>
    );
  }

  // Regular tier-based lock (premium / vip subscription)
  return (
    <div className="surface rounded-2xl p-10 lg:p-16 text-center relative overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
      <div className="relative">
        <Lock weight="fill" className="w-12 h-12 text-yellow-400 mx-auto mb-5" />
        <div className="text-[11px] text-yellow-400 tracking-[0.25em] uppercase font-bold mb-2">Contenido premium</div>
        <h2 className="text-display font-black text-3xl tracking-tight mb-3">Esta jugada es exclusiva</h2>
        <p className="text-zinc-400 max-w-md mx-auto text-sm">
          Actualiza tu plan para ver la selección, el análisis del analista, los datos del partido y la evaluación del sistema.
        </p>
        <Link to="/plans" className="btn-primary mt-6" data-testid="pick-detail-upgrade">
          <Crown weight="fill" className="w-4 h-4" /> Ver planes
        </Link>
      </div>
    </div>
  );
}


function PickVipUnlockModal({ pick, onClose, onSuccess }) {
  const [step, setStep]   = useState("choose");   // choose | manual | submitting
  const [method, setMethod] = useState("zelle");
  const [proofImage, setProofImage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);

  useEffect(() => {
    apiClient.get("/payment-methods")
      .then(r => setPaymentMethods(r.data?.methods || []))
      .catch(() => setPaymentMethods([]));
  }, []);

  const methodData = paymentMethods.find(m => m.type === method);
  const availableMethods = paymentMethods.length > 0
    ? paymentMethods.map(m => m.type)
    : ["zelle", "pago_movil", "binance"];

  const copyToClipboard = (text) => {
    if (!text) return;
    try {
      navigator.clipboard?.writeText(text);
    } catch {}
  };

  const buyWithStripe = async () => {
    setSubmitting(true);
    try {
      const r = await apiClient.post(`/picks/${pick.id}/unlock-stripe`);
      if (r.data?.checkout_url) {
        window.location.href = r.data.checkout_url;
      } else if (r.data?.already_purchased) {
        onSuccess?.();
      }
    } catch (err) {
      alert(err?.response?.data?.detail || "Error iniciando pago");
      setSubmitting(false);
    }
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert("Max 5MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setProofImage(reader.result);
    reader.readAsDataURL(file);
  };

  const submitManual = async () => {
    if (!proofImage) { alert("Sube tu comprobante"); return; }
    setSubmitting(true);
    try {
      await apiClient.post(`/picks/${pick.id}/unlock-manual`, {
        payment_method: method,
        payment_proof_image: proofImage,
      });
      alert("✅ Comprobante recibido. El admin te dará acceso en breve.");
      onClose();
    } catch (err) {
      alert(err?.response?.data?.detail || "Error subiendo comprobante");
    }
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-zinc-900 rounded-2xl border border-yellow-500/30 max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="text-center mb-6">
          <div className="text-[10px] tracking-[0.3em] uppercase font-black" style={{ color: "#D4AF37" }}>
            Pick VIP
          </div>
          <h3 className="text-2xl font-black text-white mt-1">Desbloquear por ${pick.pick_price?.toFixed(0)}</h3>
          <p className="text-xs text-zinc-500 mt-1">{pick.away_team} @ {pick.home_team}</p>
        </div>

        {step === "choose" && (
          <div className="space-y-3">
            <button
              onClick={buyWithStripe}
              disabled={submitting}
              className="w-full px-4 py-4 rounded-xl text-sm font-black tracking-widest uppercase transition-all disabled:opacity-50"
              style={{
                background: "linear-gradient(135deg, #635BFF 0%, #4F46E5 100%)",
                color: "white",
              }}
              data-testid="pick-vip-stripe-btn"
            >
              💳 Pagar con tarjeta (instantáneo)
            </button>
            <button
              onClick={() => setStep("manual")}
              className="w-full px-4 py-4 rounded-xl text-sm font-bold tracking-wider uppercase transition-all border"
              style={{ borderColor: "rgba(212,175,55,0.3)", color: "#D4AF37", background: "rgba(212,175,55,0.05)" }}
              data-testid="pick-vip-manual-btn"
            >
              📱 Zelle / Pago Móvil / Binance
            </button>
            <button onClick={onClose} className="w-full text-xs text-zinc-500 hover:text-zinc-300 py-2 mt-2">
              Cancelar
            </button>
          </div>
        )}

        {step === "manual" && (
          <div className="space-y-4">
            <div>
              <label className="text-xs text-zinc-400 uppercase tracking-wider font-bold mb-2 block">Método</label>
              <div className="grid grid-cols-3 gap-2">
                {availableMethods.map(m => (
                  <button
                    key={m}
                    onClick={() => setMethod(m)}
                    className={`px-2 py-2 rounded-lg text-xs font-bold uppercase transition-all border ${method === m ? "border-yellow-500 bg-yellow-500/10 text-yellow-400" : "border-white/10 text-zinc-400 hover:bg-white/5"}`}
                    data-testid={`pick-vip-method-${m}`}
                  >
                    {m === "pago_movil" ? "Pago Móvil" : m.charAt(0).toUpperCase() + m.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {methodData && (
              <div
                className="rounded-xl p-4 border border-yellow-500/30 space-y-2.5"
                style={{ background: "rgba(212,175,55,0.06)" }}
                data-testid="pick-vip-account-data"
              >
                <div className="text-[10px] uppercase tracking-widest font-bold text-yellow-400 mb-1">
                  Envía el pago a estos datos
                </div>
                {methodData.account_name && (
                  <div className="flex justify-between items-center gap-2">
                    <span className="text-[11px] text-zinc-500 uppercase font-bold">Titular</span>
                    <span className="text-sm font-bold text-white text-right">{methodData.account_name}</span>
                  </div>
                )}
                <div className="flex justify-between items-center gap-2">
                  <span className="text-[11px] text-zinc-500 uppercase font-bold">Cuenta</span>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(methodData.account_info)}
                    className="text-sm font-mono font-black text-yellow-400 text-right hover:underline break-all"
                    title="Toca para copiar"
                  >
                    {methodData.account_info}
                  </button>
                </div>
                <div className="flex justify-between items-center gap-2 pt-1 border-t border-white/[0.05]">
                  <span className="text-[11px] text-zinc-500 uppercase font-bold">Monto</span>
                  <span className="text-base font-black text-emerald-400">${pick.pick_price?.toFixed(2)}</span>
                </div>
                {methodData.instructions && (
                  <p className="text-[11px] text-zinc-400 leading-relaxed pt-2 border-t border-white/[0.05]">
                    {methodData.instructions}
                  </p>
                )}
              </div>
            )}

            <div>
              <label className="text-xs text-zinc-400 uppercase tracking-wider font-bold mb-2 block">Comprobante</label>
              <input type="file" accept="image/*" onChange={onFile} className="text-xs text-zinc-300 w-full" data-testid="pick-vip-proof-input" />
              {proofImage && <img src={proofImage} alt="Comprobante" className="mt-2 max-h-40 rounded-lg" />}
            </div>
            <div className="flex gap-2">
              <button onClick={() => setStep("choose")} className="px-4 py-3 rounded-xl text-xs font-bold uppercase border border-white/10 text-zinc-400">
                Atrás
              </button>
              <button
                onClick={submitManual}
                disabled={submitting || !proofImage}
                className="flex-1 px-4 py-3 rounded-xl text-xs font-black uppercase tracking-wider disabled:opacity-50"
                style={{ background: "#D4AF37", color: "#0A0A0F" }}
                data-testid="pick-vip-submit-manual"
              >
                {submitting ? "Enviando…" : "Enviar comprobante"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
